import { useCallback, useEffect, useRef, useState } from 'react';
import { normalizeOption } from '../utils/popupUtils';

const SEARCH_DEBOUNCE_MS = 500;
const SEARCH_MIN_LENGTH = 3;

/**
 * Provides debounced search handlers for search-select and mention-textarea fields.
 *
 * Each handler uses AbortController (latest-request-wins): when a new search fires
 * before the previous one resolves, the stale response is discarded.
 *
 * A search-select field also gets a PRELOAD: the moment the popup opens, its
 * fetcher is called once with an empty term so the dropdown already lists the
 * most recent records instead of being blank until 3 characters are typed. The
 * fetcher decides what "empty term" means (the Quick Submit endpoints return the
 * 10 most recent jobs / candidates). Opt out per field with
 * `preloadOptions: false`.
 *
 * @param {Object} params
 * @param {Object} params.api - { searchOptions?, getMentionOptions? }
 * @param {boolean} params.open
 * @param {Array} [params.fields] - Resolved fields, used to decide what to preload.
 * @param {Object} params.popupContext
 * @param {Function} params.setDynamicOptions - Setter from usePopupOptions
 * @returns {{ mentionOptions: Object, getSearchHandler: Function, getMentionSearchHandler: Function }}
 */
export function usePopupSearch({ api, open, fields = [], popupContext, setDynamicOptions }) {
  const [mentionOptions, setMentionOptions] = useState({});
  const searchTimerRef = useRef({});
  const searchAbortRef = useRef({});
  const mentionAbortRef = useRef({});

  /** The one place a search-select's options are fetched and stored, shared by
   *  the open-time preload and the debounced onSearch handler. Returns the
   *  AbortController driving this call so a caller can cancel it directly. */
  const runSearch = useCallback((field, search) => {
    const searchFn = api.searchOptions ?? api.searchFetcher;
    if (typeof searchFn !== 'function') return undefined;

    // Cancel the previous in-flight request for this field
    searchAbortRef.current[field.name]?.abort();
    const controller = new AbortController();
    searchAbortRef.current[field.name] = controller;

    Promise.resolve(searchFn(field, search, popupContext))
      .then((data) => {
        if (controller.signal.aborted) return;
        const raw = Array.isArray(data) ? data : (data?.items ?? data?.list ?? []);
        setDynamicOptions((prev) => ({
          ...prev,
          [field.name]: raw.map((item) => normalizeOption(item, field)),
        }));
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        console.error(`[DynamicPopup] Search failed for field "${field.name}":`, err);
      });
    return controller;
  }, [api, popupContext, setDynamicOptions]);

  // ── Open-time preload ───────────────────────────────────────────────────────
  // The fields to preload, and the latest closure that can fetch them, are held
  // in refs rather than passed as effect dependencies. DynamicPopupRoute builds
  // its `api` prop as a fresh object literal on EVERY render, so `runSearch`'s
  // identity changes every render; depending on it here would mean
  // effect → setDynamicOptions → re-render → effect, an endless fetch loop.
  // The effect therefore keys only on [open, preloadKey] — the popup opening,
  // or the set of preloadable field names actually changing.
  const preloadFields = fields.filter(
    (field) => (field.searchFetchKey || field.searchFetcher) && field.preloadOptions !== false,
  );
  const preloadKey = preloadFields.map((field) => field.name).join('|');

  const preloadRef = useRef({ runSearch, preloadFields });
  // Kept fresh in an effect, not during render (refs must not be written while
  // rendering). Effects run in declaration order, so this one has always
  // updated the ref by the time the preload effect below reads it.
  useEffect(() => {
    preloadRef.current = { runSearch, preloadFields };
  });

  useEffect(() => {
    if (!open || !preloadKey) return undefined;

    const { runSearch: run, preloadFields: fieldsToLoad } = preloadRef.current;
    const controllers = fieldsToLoad.map((field) => run(field, '')).filter(Boolean);

    return () => controllers.forEach((controller) => controller.abort());
  }, [open, preloadKey]);

  /**
   * Returns a debounced `onSearch` handler for a search-select field,
   * or `undefined` if the field has no search fetch key.
   * @param {Object} field
   * @returns {Function|undefined}
   */
  function getSearchHandler(field) {
    if (!field.searchFetchKey && !field.searchFetcher) return undefined;

    const searchFn = api.searchOptions ?? api.searchFetcher;
    if (typeof searchFn !== 'function') return undefined;

    const minLength = field.searchMinLength ?? SEARCH_MIN_LENGTH;

    return (search) => {
      clearTimeout(searchTimerRef.current[field.name]);

      // Below the minimum (including a cleared box) the field falls BACK to the
      // recent list rather than blanking the dropdown, so there is always
      // something to pick. Passing '' is what tells the fetcher "recent, not
      // filtered" — the same call the preload above makes.
      const term = search.length < minLength ? '' : search;

      searchTimerRef.current[field.name] = setTimeout(() => {
        runSearch(field, term);
      }, SEARCH_DEBOUNCE_MS);
    };
  }

  /**
   * Returns an `onSearch` handler for a mention-textarea field,
   * or `undefined` if no mention fetcher is configured.
   * @param {Object} field
   * @returns {Function|undefined}
   */
  function getMentionSearchHandler(field) {
    const mentionFn = api.getMentionOptions ?? api.mentionFetcher;
    if (typeof mentionFn !== 'function') return undefined;

    return (search) => {
      // Cancel the previous in-flight mention request for this field
      mentionAbortRef.current[field.name]?.abort();
      const controller = new AbortController();
      mentionAbortRef.current[field.name] = controller;

      Promise.resolve(mentionFn(field, search, popupContext))
        .then((data) => {
          if (controller.signal.aborted) return;
          const raw = Array.isArray(data) ? data : (data?.items ?? data?.list ?? []);
          setMentionOptions((prev) => ({
            ...prev,
            [field.name]: raw.map((item) => ({
              value: item.user_name ?? item.userName ?? item.username ?? String(item),
              label: item.user_name ?? item.userName ?? item.username ?? String(item),
            })),
          }));
        })
        .catch((err) => {
          if (controller.signal.aborted) return;
          console.error(`[DynamicPopup] Mention search failed for field "${field.name}":`, err);
        });
    };
  }

  return { mentionOptions, getSearchHandler, getMentionSearchHandler };
}
